// Order detail — summary, line items, activity timeline, and (staff, pending) approve/reject.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import PaymentPanel from '../components/PaymentPanel.jsx';
import ShippingPanel from '../components/ShippingPanel.jsx';
import ShippingAddressCard from '../components/ShippingAddressCard.jsx';
import OrderLineEditor from '../components/OrderLineEditor.jsx';
import OrderAdjustmentEditor, { AdjustmentHistory } from '../components/OrderAdjustmentEditor.jsx';
import { getOrder, approveOrder, rejectOrder, addOrderNote, invoiceUrl, sendInvoice, cancelOrder, releaseOrder, listAdjustments, withdrawOrder, setOrderPo } from '../api/orders.js';
import { fmtMoney, sizeOf } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { storeName } from '../lib/stores.js';
import { can, CAP } from '../lib/access.js';
import { approvalReasonText } from '../lib/approval.js';
import { eventLabel, PROBLEM_EVENTS } from '../lib/labels.js';
import OrderNextStep from '../components/OrderNextStep.jsx';

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isStaff = role === 'INTERNAL_STAFF';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // `error` is ONLY for "couldn't load this order" — it replaces the whole page. A failed ACTION
  // (approve before shipping is confirmed, a send that bounced) must not wipe the screen you need to
  // fix it on, so those go to `actionError`, shown as a dismissible banner.
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [acting, setActing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [panel, setPanel] = useState(null); // 'invoice' | 'cancel' | null
  const [invoiceForm, setInvoiceForm] = useState({ subject: '', message: '' });
  const [cancelForm, setCancelForm] = useState({ reason: 'OTHER', note: '', notifyCustomer: true });
  const [rejectForm, setRejectForm] = useState({ note: '', notifyCustomer: true });
  const [withdrawReason, setWithdrawReason] = useState('');
  const [poEdit, setPoEdit] = useState(null); // string while editing the PO number
  const [panelBusy, setPanelBusy] = useState(false);
  const [flash, setFlash] = useState('');
  const [editing, setEditing] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [paymentsVersion, setPaymentsVersion] = useState(0); // bump → PaymentPanel reloads
  const [overrideOpen, setOverrideOpen] = useState(false); // credit-hold "approve anyway" step
  const [overrideNote, setOverrideNote] = useState('');

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setNoteBusy(true);
    try {
      await addOrderNote(id, noteText.trim());
      setNoteText('');
      load();
    } finally {
      setNoteBusy(false);
    }
  };

  const load = () => {
    setLoading(true);
    getOrder(id)
      .then(setOrder)
      .catch((e) => setError(e.response?.data?.error?.message || 'Order not found.'))
      .finally(() => setLoading(false));
    listAdjustments(id).then(setAdjustments).catch(() => setAdjustments([]));
  };
  useEffect(load, [id]);

  const decide = async (kind, { override = false } = {}) => {
    // An account on hold / over its credit limit is approved only on an explicit override + reason.
    if (kind === 'approve' && !override && order?.credit?.blocked) {
      setActionError('');
      setOverrideOpen(true);
      return;
    }
    setActing(true);
    try {
      if (kind === 'approve') await approveOrder(id, override ? overrideNote.trim() : undefined, { override });
      else await rejectOrder(id, rejectForm.note.trim() || undefined, { notifyCustomer: rejectForm.notifyCustomer });
      setPanel(null);
      setOverrideOpen(false);
      setOverrideNote('');
      load();
    } catch (e) {
      // The credit position can change after the page loaded (e.g. a payment went overdue): offer the override.
      if (e.response?.data?.error?.code === 'CREDIT_HOLD') setOverrideOpen(true);
      setActionError(e.response?.data?.error?.message || 'Action failed.');
    } finally {
      setActing(false);
    }
  };

  const openInvoice = () => {
    setFlash('');
    setActionError('');
    setInvoiceForm({
      subject: '',
      message: `Hi there,\n\nPlease find attached your invoice for order ${order.orderNumber || order.id.slice(-8)}.\n\nIf you have any questions, just reply to this email.\n\nThank you,\nTEAMM8 Wholesale`,
      includeLink: order.status !== 'REJECTED' && order.paymentStatus !== 'PAID_IN_FULL',
    });
    setPanel((p) => (p === 'invoice' ? null : 'invoice'));
  };
  const doSendInvoice = async () => {
    setPanelBusy(true);
    setActionError('');
    setFlash('');
    try {
      const r = await sendInvoice(id, {
        message: invoiceForm.message || undefined,
        subject: invoiceForm.subject || undefined,
        includePaymentLink: invoiceForm.includeLink,
      });
      setPanel(null);
      const withLink = r.paymentLink ? ' with a payment link' : '';
      setFlash(
        r.simulated
          ? `Invoice ready${withLink} — email delivery is simulated in dev (would go to ${r.to}).`
          : `Invoice ${r.invoiceNumber} emailed${withLink} to ${r.to}.`
      );
      load();
    } catch (e) {
      setActionError(e.response?.data?.error?.message || 'Could not send the invoice.');
    } finally {
      setPanelBusy(false);
    }
  };

  const doRelease = async () => {
    if (
      !window.confirm(
        'Release this order to fulfilment now, before payment?\n\nThis marks it paid in Shopify to trigger 3PL dispatch — it does NOT record a client payment. The payment schedule is unchanged.'
      )
    )
      return;
    setActionError('');
    setFlash('');
    try {
      await releaseOrder(id);
      setFlash('Released to fulfilment — dispatch triggered in Shopify.');
      load();
    } catch (e) {
      setActionError(e.response?.data?.error?.message || 'Could not release the order.');
    }
  };

  const openCancel = () => {
    setFlash('');
    setActionError('');
    setCancelForm({ reason: 'OTHER', note: '', notifyCustomer: true });
    setPanel((p) => (p === 'cancel' ? null : 'cancel'));
  };
  const doCancel = async () => {
    setPanelBusy(true);
    setActionError('');
    setFlash('');
    try {
      await cancelOrder(id, { reason: cancelForm.reason, note: cancelForm.note || undefined, notifyCustomer: cancelForm.notifyCustomer });
      setPanel(null);
      setFlash(`Order cancelled (and cancelled in Shopify).${cancelForm.notifyCustomer ? ' The client has been emailed.' : ''}`);
      load();
    } catch (e) {
      setActionError(e.response?.data?.error?.message || 'Could not cancel the order.');
    } finally {
      setPanelBusy(false);
    }
  };

  const doWithdraw = async () => {
    setPanelBusy(true);
    setActionError('');
    try {
      await withdrawOrder(id, withdrawReason.trim() || undefined);
      setPanel(null);
      setFlash('Order withdrawn. The TEAMM8 team has been told.');
      load();
    } catch (e) {
      setActionError(e.response?.data?.error?.message || 'Could not withdraw the order.');
    } finally {
      setPanelBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-text-muted">Loading…</div>;
  if (error) return <div className="text-sm text-error">{error}</div>;
  if (!order) return null;

  const canDecide = can(user, CAP.APPROVE) && order.status === 'PENDING_APPROVAL';
  const canReorder = (role === 'CLIENT' || role === 'REP') && order.lineItems?.length > 0;
  // Staff may edit a still-draft order (add/remove products, quantities, prices) until it ships or a
  // payment is recorded. The server enforces the same window; this just gates the button.
  const canEditOrder =
    isStaff &&
    can(user, CAP.CREATE_ORDER) &&
    !order.dispatched &&
    !order.archivedAt &&
    order.status !== 'REJECTED' &&
    order.status !== 'CANCELLED' &&
    order.paymentStatus === 'UNPAID';

  // CS can remove out-of-stock items from an approved, not-yet-shipped order of a link-paying client
  // (refunding any paid share). The server enforces all of this; this just gates the button.
  const canAdjust =
    isStaff &&
    can(user, CAP.MANAGE_PAYMENTS) &&
    order.status === 'APPROVED' &&
    !order.archivedAt &&
    !order.billingBatchId &&
    order.companyPaymentMethod === 'LINK' &&
    !['FULFILLED', 'DELIVERED'].includes(order.fulfillmentState);

  const onAdjusted = (r) => {
    setAdjusting(false);
    setActionError('');
    const a = r?.adjustment;
    let msg = 'Items removed.';
    if (a?.refundTotal > 0) msg += ` Refunded ${fmtMoney(a.refundTotal, order.currency)} to the client’s original payment method.`;
    if (a?.shopifySynced) msg += ' Shopify updated.';
    if (a?.invoiceReissued) msg += ' Revised invoice emailed to the client.';
    if (r?.refundFailure) msg += ` ⚠ A refund failed (${r.refundFailure.label}) — issue it in the Airwallex dashboard.`;
    if (a?.shopifyError) msg += ` ⚠ Shopify wasn’t updated: ${a.shopifyError}`;
    setFlash(msg);
    setPaymentsVersion((v) => v + 1);
    load();
  };

  // A client or rep can change or withdraw their own order while it's still awaiting approval.
  const canSelfServe = (role === 'CLIENT' || role === 'REP') && order.status === 'PENDING_APPROVAL' && !order.archivedAt;

  // Shipping changes the total and gates approval — refresh the order + payments after any change.
  const onShippingChanged = () => {
    setPaymentsVersion((v) => v + 1);
    load();
  };
  const onShippingAutoApproved = () => {
    setFlash('Shipping confirmed — this trusted client’s order was approved automatically and the invoice sent.');
    onShippingChanged();
  };

  const onEdited = (updated) => {
    setEditing(false);
    setActionError('');
    let msg = 'Order updated — changes synced to Shopify.';
    if (updated?.shopifyWarning === 'price_override_not_synced') {
      msg = 'Order updated. Quantities synced to Shopify, but the custom price could not be pushed to the Shopify draft — the platform total (used for billing) is correct.';
    } else if (updated?.shopifySynced === false) {
      msg = 'Order updated on the platform (this order has no live Shopify draft to sync).';
    }
    if (updated?.invoiceReissued) msg += ' A corrected invoice + payment link were emailed to the client.';
    if (!isStaff) msg = 'Order updated. The TEAMM8 team will review the change (shipping may be re-confirmed).';
    setFlash(msg);
    load();
  };

  return (
    <>
      <button onClick={() => navigate(role === 'CLIENT' ? '/' : '/orders')} className="mb-3 text-sm text-text-secondary hover:text-text-primary">
        ← {role === 'CLIENT' ? 'My orders' : 'Orders'}
      </button>
      <PageHeader
        title={`Order ${order.orderNumber || order.id.slice(-8)}`}
        subtitle={`${order.company || ''} · ${storeName(order.store)}`}
        actions={
          <>
            <a
              href={invoiceUrl(order.id)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
            >
              Invoice PDF
            </a>
            {isStaff && order.status === 'APPROVED' && (
              <button
                onClick={openInvoice}
                title="Email the invoice (and the current payment link) again"
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
              >
                Send invoice
              </button>
            )}
            {canEditOrder && !editing && (
              <button
                onClick={() => {
                  setFlash('');
                  setActionError('');
                  setEditing(true);
                }}
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
              >
                Edit order
              </button>
            )}
            {canAdjust && !adjusting && !editing && (
              <button
                onClick={() => {
                  setFlash('');
                  setActionError('');
                  setAdjusting(true);
                }}
                title="Remove items that are out of stock and refund what the client already paid for them"
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
              >
                Out of stock / refund
              </button>
            )}
            {isStaff && can(user, CAP.MANAGE_SHIPPING) && order.status === 'APPROVED' && !order.dispatched && (
              <button
                onClick={doRelease}
                title="Convert the draft to a paid Shopify order now to trigger 3PL dispatch (before the client has paid)"
                className="rounded-md border border-accent/50 px-3 py-1.5 text-sm font-medium text-accent hover:bg-accent-light"
              >
                Release to fulfilment
              </button>
            )}
            {isStaff && order.status === 'APPROVED' && (
              <button
                onClick={openCancel}
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-error hover:bg-error-light"
              >
                Cancel order
              </button>
            )}
            {canReorder && (
              <button
                onClick={() =>
                  navigate('/catalog', {
                    state: {
                      reorder: order.lineItems.map((l) => ({ variantId: l.variantId, quantity: l.quantity })),
                      // Reps/staff: reorder for the SAME client — never whichever client gets picked next.
                      ...(role !== 'CLIENT' && order.client?.shopifyCompanyLocationId
                        ? { client: { store: order.store, shopifyCompanyLocationId: order.client.shopifyCompanyLocationId, companyName: order.client.company || order.company, locationName: order.client.location || null } }
                        : {}),
                    },
                  })
                }
                className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
              >
                Reorder
              </button>
            )}
            {canSelfServe && !editing && (
              <>
                <button
                  onClick={() => {
                    setFlash('');
                    setActionError('');
                    setEditing(true);
                  }}
                  className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
                >
                  Change order
                </button>
                <button
                  onClick={() => {
                    setWithdrawReason('');
                    setPanel((p) => (p === 'withdraw' ? null : 'withdraw'));
                  }}
                  className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-error hover:bg-error-light"
                >
                  Withdraw order
                </button>
              </>
            )}
            {canDecide && (
              <>
                <button
                  onClick={() => {
                    setRejectForm({ note: '', notifyCustomer: true });
                    setPanel((p) => (p === 'reject' ? null : 'reject'));
                  }}
                  disabled={acting}
                  className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-error hover:bg-error-light disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  onClick={() => decide('approve')}
                  disabled={acting || order.shippingState !== 'CONFIRMED'}
                  title={order.shippingState !== 'CONFIRMED' ? 'Confirm the shipping cost first (Shipping panel)' : 'Approves the order and emails the invoice + payment link to the client'}
                  className="rounded-md bg-success px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  Approve &amp; email invoice
                </button>
              </>
            )}
          </>
        }
      />

      {/* Credit control (staff): account on hold / over its credit limit. Gates approval. */}
      {isStaff && order.credit?.blocked && (
        <div className="mb-4 rounded-md border border-error/40 bg-error-light px-3 py-2.5 text-sm text-text-primary">
          <div className="font-semibold text-error">
            {order.credit.onHold ? 'Account on hold' : 'Over credit limit'}
            {order.status === 'PENDING_APPROVAL' && ' — approving needs an override'}
          </div>
          <ul className="mt-1 list-disc pl-5 text-xs text-text-secondary">
            {order.credit.reasons.map((r) => (
              <li key={r.code}>{r.message}</li>
            ))}
          </ul>
          {overrideOpen && canDecide && (
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <label className="min-w-[16rem] flex-1 text-xs text-text-secondary">
                Reason for approving anyway (logged on the order)
                <input
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  maxLength={500}
                  placeholder="e.g. client paid today, remittance received"
                  className="mt-0.5 block w-full rounded border border-line bg-surface px-2 py-1 text-sm"
                />
              </label>
              <button onClick={() => setOverrideOpen(false)} className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-text-secondary">
                Cancel
              </button>
              <button
                onClick={() => decide('approve', { override: true })}
                disabled={acting || !overrideNote.trim()}
                className="rounded-md bg-error px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {acting ? 'Approving…' : 'Approve anyway'}
              </button>
            </div>
          )}
        </div>
      )}

      <OrderNextStep
        order={order}
        role={role}
        refreshKey={paymentsVersion}
        onPaid={() => {
          setFlash('Payment received — thank you!');
          setPaymentsVersion((v) => v + 1);
          load();
        }}
        staffApproval={
          canDecide ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className={`rounded-md border px-3 py-2 ${order.shippingState === 'CONFIRMED' ? 'border-success/30 bg-success-light/40' : 'border-accent/40 bg-accent-light/50'}`}>
                <div className="text-sm font-semibold text-text-primary">① Confirm shipping {order.shippingState === 'CONFIRMED' && '✓'}</div>
                <div className="text-xs text-text-secondary">
                  {order.shippingState === 'CONFIRMED'
                    ? `Confirmed — ${order.shippingCost > 0 ? fmtMoney(order.shippingCost, order.currency) : 'free shipping'}`
                    : 'Check the lane and cost in the Shipping panel, then confirm it.'}
                </div>
                {order.shippingState !== 'CONFIRMED' && (
                  <a href="#shipping" className="mt-1 inline-block text-xs font-medium text-accent hover:underline">
                    Go to shipping ↓
                  </a>
                )}
              </div>
              <div className={`rounded-md border px-3 py-2 ${order.shippingState === 'CONFIRMED' ? 'border-accent/40 bg-accent-light/50' : 'border-line bg-surface-raised/40'}`}>
                <div className="text-sm font-semibold text-text-primary">② Approve &amp; email invoice</div>
                <div className="text-xs text-text-secondary">Opens payment and emails the invoice + payment link to the client. Or reject with a reason.</div>
              </div>
            </div>
          ) : null
        }
      />

      {/* Sent-to-warehouse badge (staff) — the draft became a real Shopify order, so the 3PL can pick it. */}
      {isStaff && order.dispatched && (
        <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success-light px-3 py-1 text-xs font-medium text-success">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          Sent to the warehouse
        </div>
      )}

      {/* Prominent tracking banner — shown once the order has shipped (tracking synced from Shopify). */}
      {order.trackingNumbers?.length > 0 && (
        <div className="mb-4 rounded-lg border border-success/40 bg-success-light/50 px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-success">Shipped · Tracking</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1">
                {order.trackingNumbers.map((t, i) => (
                  <a
                    key={t}
                    href={order.trackingUrls?.[i] || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="text-2xl font-bold tracking-tight text-text-primary hover:text-accent hover:underline"
                  >
                    {t}
                  </a>
                ))}
              </div>
            </div>
            {order.trackingUrls?.[0] && (
              <a
                href={order.trackingUrls[0]}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
              >
                Track shipment →
              </a>
            )}
          </div>
        </div>
      )}

      {flash && (
        <div className="mb-4 rounded-md border border-success/40 bg-success-light px-3 py-2 text-sm text-success">{flash}</div>
      )}
      {actionError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-error/40 bg-error-light px-3 py-2 text-sm text-error">
          <span>{actionError}</span>
          <button onClick={() => setActionError('')} className="text-xs underline hover:no-underline" aria-label="Dismiss error">
            Dismiss
          </button>
        </div>
      )}

      {panel === 'invoice' && (
        <div className="mb-4 max-w-2xl rounded-lg border border-line bg-surface p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-text-primary">Send invoice to the client</h3>
          <p className="mt-1 text-xs text-text-muted">
            Emails the invoice PDF to the client&rsquo;s primary contact (the assigned rep is CC&rsquo;d). Tweak the message if you like — a saved default is used when you leave a field blank.
          </p>
          {order.shippingState !== 'CONFIRMED' && (
            <div className="mt-3 rounded-md border border-warning/40 bg-warning-light/50 px-3 py-2 text-xs text-warning">
              ⚠ Shipping cost isn&rsquo;t confirmed yet — this invoice total may not include shipping. Confirm shipping in the panel below first, then send. You can still send now if you mean to.
            </div>
          )}
          <label className="mt-3 block text-xs font-medium text-text-muted">
            Subject <span className="font-normal text-text-muted/70">(blank = saved default)</span>
            <input
              value={invoiceForm.subject}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, subject: e.target.value })}
              placeholder="Your TEAMM8 Wholesale invoice"
              className="mt-1 w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          <label className="mt-3 block text-xs font-medium text-text-muted">
            Message
            <textarea
              value={invoiceForm.message}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, message: e.target.value })}
              rows={7}
              className="mt-1 w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          <label className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={invoiceForm.includeLink}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, includeLink: e.target.checked })}
              className="accent-accent"
            />
            Also create &amp; email a payment link (for the next amount due)
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setPanel(null)} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised">
              Cancel
            </button>
            <button
              onClick={doSendInvoice}
              disabled={panelBusy}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {panelBusy ? 'Sending…' : 'Send invoice'}
            </button>
          </div>
        </div>
      )}

      {panel === 'reject' && (
        <div className="mb-4 max-w-2xl rounded-lg border border-error/30 bg-surface p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-error">Reject this order</h3>
          <label className="mt-2 block text-xs font-medium text-text-muted">
            Reason
            <input
              value={rejectForm.note}
              onChange={(e) => setRejectForm({ ...rejectForm, note: e.target.value })}
              maxLength={500}
              placeholder="e.g. Some styles are discontinued — please re-order from the current range"
              className="mt-1 w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          <label className="mt-2 flex items-center gap-2 text-xs text-text-secondary">
            <input type="checkbox" checked={rejectForm.notifyCustomer} onChange={(e) => setRejectForm({ ...rejectForm, notifyCustomer: e.target.checked })} className="accent-accent" />
            Email the client that the order wasn’t accepted (with this reason)
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setPanel(null)} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised">
              Back
            </button>
            <button
              onClick={() => decide('reject')}
              disabled={acting || (rejectForm.notifyCustomer && !rejectForm.note.trim())}
              title={rejectForm.notifyCustomer && !rejectForm.note.trim() ? 'Add a reason for the client' : undefined}
              className="rounded-md bg-error px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {acting ? 'Rejecting…' : 'Reject order'}
            </button>
          </div>
        </div>
      )}

      {panel === 'withdraw' && (
        <div className="mb-4 max-w-2xl rounded-lg border border-error/30 bg-surface p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-error">Withdraw this order?</h3>
          <p className="mt-1 text-xs text-text-muted">It hasn’t been approved yet, so nothing has been invoiced or charged. The TEAMM8 team will be told.</p>
          <label className="mt-2 block text-xs font-medium text-text-muted">
            Reason (optional)
            <input
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Ordered the wrong sizes — placing a new order"
              className="mt-1 w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setPanel(null)} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised">
              Keep order
            </button>
            <button onClick={doWithdraw} disabled={panelBusy} className="rounded-md bg-error px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
              {panelBusy ? 'Withdrawing…' : 'Withdraw order'}
            </button>
          </div>
        </div>
      )}

      {panel === 'cancel' && (
        <div className="mb-4 max-w-2xl rounded-lg border border-error/30 bg-surface p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-error">Cancel this order</h3>
          <p className="mt-1 text-xs text-text-muted">
            This also cancels the order in Shopify and restocks the inventory. No Shopify refund is issued — any refund of payments taken in the platform / Airwallex is handled separately.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <label className="block text-xs font-medium text-text-muted">
              Reason
              <select
                value={cancelForm.reason}
                onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                className="mt-1 block rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm"
              >
                <option value="OTHER">Other</option>
                <option value="CUSTOMER">Customer changed their mind</option>
                <option value="INVENTORY">Out of stock</option>
                <option value="DECLINED">Payment declined</option>
                <option value="FRAUD">Fraud</option>
                <option value="STAFF">Staff decision</option>
              </select>
            </label>
            <label className="block flex-1 text-xs font-medium text-text-muted">
              Note (optional — sent to the client as the reason if emailed)
              <input
                value={cancelForm.note}
                onChange={(e) => setCancelForm({ ...cancelForm, note: e.target.value })}
                placeholder="e.g. duplicate of T8AU0123"
                className="mt-1 w-full rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-text-secondary">
            <input type="checkbox" checked={cancelForm.notifyCustomer} onChange={(e) => setCancelForm({ ...cancelForm, notifyCustomer: e.target.checked })} className="accent-accent" />
            Email the client that the order is cancelled (the note is included as the reason)
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setPanel(null)} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised">
              Keep order
            </button>
            <button
              onClick={doCancel}
              disabled={panelBusy}
              className="rounded-md bg-error px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {panelBusy ? 'Cancelling…' : 'Cancel order'}
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {adjusting ? (
            <OrderAdjustmentEditor order={order} onDone={onAdjusted} onCancel={() => setAdjusting(false)} />
          ) : editing ? (
            <OrderLineEditor order={order} onSaved={onEdited} onCancel={() => setEditing(false)} allowPriceOverride={isStaff} />
          ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                  <th className="px-4 py-2 font-medium">Style</th>
                  <th className="px-4 py-2 font-medium">Size</th>
                  <th className="px-4 py-2 font-medium">SKU</th>
                  <th className="px-4 py-2 text-right font-medium">Qty</th>
                  <th className="px-4 py-2 text-right font-medium">Unit</th>
                  <th className="px-4 py-2 text-right font-medium">Line total</th>
                </tr>
              </thead>
              <tbody>
                {order.lineItems?.map((l, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    <td className="px-4 py-2 text-text-primary">{l.title}</td>
                    <td className="px-4 py-2 text-text-secondary">{sizeOf(l)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-text-muted">
                      {l.sku}
                      {l.backordered && (
                        <span className="ml-1.5 rounded bg-warning-light px-1.5 py-0.5 font-sans text-[10px] font-medium text-warning" title="More than was in stock when ordered — ships when restocked">
                          Backorder
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{l.quantity}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                      {fmtMoney(l.unitPrice, order.currency)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums">
                      {fmtMoney(l.lineTotal, order.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-raised/40 text-sm font-semibold">
                  <td className="px-4 py-2" colSpan={3}>
                    Goods subtotal
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{order.unitCount}</td>
                  <td />
                  <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(order.subtotal, order.currency)}</td>
                </tr>
                {order.discountAmount > 0 && (
                  <tr className="text-sm">
                    <td className="px-4 py-1.5 text-text-secondary" colSpan={5}>
                      Discount{order.discountName ? ` — ${order.discountName}` : ''}{order.discountPercent ? ` (${order.discountPercent}%)` : ''}
                    </td>
                    <td className="px-4 py-1.5 text-right tabular-nums text-text-secondary">−{fmtMoney(order.discountAmount, order.currency)}</td>
                  </tr>
                )}
                <tr className="text-sm">
                  <td className="px-4 py-1.5 text-text-secondary" colSpan={5}>
                    Shipping
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-text-secondary">
                    {order.shippingState === 'CONFIRMED'
                      ? order.shippingCost > 0
                        ? fmtMoney(order.shippingCost, order.currency)
                        : 'Free'
                      : order.shippingCost > 0
                        ? `${fmtMoney(order.shippingCost, order.currency)} (estimate)`
                        : 'To be confirmed'}
                  </td>
                </tr>
                <tr className="border-t border-line text-sm font-semibold">
                  <td className="px-4 py-2" colSpan={5}>
                    {order.shippingState === 'CONFIRMED' ? 'Order total' : order.shippingCost > 0 ? 'Estimated total' : 'Total before shipping'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(order.payableTotal, order.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
          {/* Customer PO: staff can always edit it; the client / rep while the order is still pending. */}
          {(order.poNumber || isStaff || canSelfServe) && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface p-3 text-sm">
              <span className="text-text-muted">Customer PO:</span>
              {poEdit === null ? (
                <>
                  <span className="font-medium text-text-primary">{order.poNumber || '—'}</span>
                  {(isStaff || canSelfServe) && (
                    <button onClick={() => setPoEdit(order.poNumber || '')} className="text-xs text-accent hover:underline">
                      {order.poNumber ? 'Change' : 'Add'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <input value={poEdit} onChange={(e) => setPoEdit(e.target.value)} maxLength={60} aria-label="Customer PO number" className="rounded-md border border-line px-2 py-1 text-sm" />
                  <button
                    onClick={async () => {
                      try {
                        await setOrderPo(id, poEdit.trim());
                        setPoEdit(null);
                        load();
                      } catch (e) {
                        setActionError(e.response?.data?.error?.message || 'Could not save the PO number.');
                      }
                    }}
                    className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover"
                  >
                    Save
                  </button>
                  <button onClick={() => setPoEdit(null)} className="text-xs text-text-muted hover:underline">
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}
          {order.note && (
            <p className="mt-3 rounded-md border border-line bg-surface p-3 text-sm text-text-secondary">
              <span className="text-text-muted">Note: </span>
              {order.note}
            </p>
          )}
          {/* Payments + adjustments sit with the order contents — the money is what most people come here for. */}
          <div className="mt-4 space-y-4">
            {order.status !== 'REJECTED' && order.status !== 'CANCELLED' && (
              <PaymentPanel key={paymentsVersion} orderId={order.id} role={role} />
            )}
            <AdjustmentHistory adjustments={adjustments} currency={order.currency} isStaff={isStaff} />
          </div>
        </div>

        <div className="space-y-4">
          {isStaff && order.status === 'PENDING_APPROVAL' && (
            <div id="shipping" className="scroll-mt-4">
              <ShippingPanel orderId={order.id} onAutoApproved={onShippingAutoApproved} onChanged={onShippingChanged} />
            </div>
          )}

          {role !== 'CLIENT' && order.client && (order.client.company || order.client.contactName || order.client.contactPhone) && (
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-text-primary">Client details</h3>
              <dl className="grid grid-cols-[6rem_1fr] gap-y-1.5 text-sm">
                <dt className="text-text-muted">Company</dt>
                <dd className="text-text-primary">{order.client.company || '—'}</dd>
                {order.client.location && (
                  <>
                    <dt className="text-text-muted">Location</dt>
                    <dd className="text-text-primary">{order.client.location}</dd>
                  </>
                )}
                <dt className="text-text-muted">Contact</dt>
                <dd className="text-text-primary">{order.client.contactName || '—'}</dd>
                {order.client.contactEmail && (
                  <>
                    <dt className="text-text-muted">Email</dt>
                    <dd className="truncate text-text-primary">
                      <a className="hover:text-accent hover:underline" href={`mailto:${order.client.contactEmail}`}>{order.client.contactEmail}</a>
                    </dd>
                  </>
                )}
                {order.client.contactPhone && (
                  <>
                    <dt className="text-text-muted">Phone</dt>
                    <dd className="text-text-primary">
                      <a className="hover:text-accent hover:underline" href={`tel:${order.client.contactPhone}`}>{order.client.contactPhone}</a>
                    </dd>
                  </>
                )}
                {order.client.destinationCountry && (
                  <>
                    <dt className="text-text-muted">Ships to</dt>
                    <dd className="text-text-primary">{order.client.destinationCountry}</dd>
                  </>
                )}
              </dl>
            </div>
          )}

          {/* Read-only shipping address (from Shopify). Client sees their own; staff/rep see the order's location. */}
          {(role === 'CLIENT' || order.client?.shopifyCompanyLocationId) && (
            <ShippingAddressCard
              readOnly
              title="Shipping address"
              scope={role === 'CLIENT' ? undefined : { store: order.store, companyLocationId: order.client.shopifyCompanyLocationId }}
            />
          )}

          <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-text-primary">Status</span>
              <StatusBadge status={order.status} role={role} />
            </div>
            <dl className="grid grid-cols-[6rem_1fr] gap-y-1.5 text-sm">
              {isStaff && (
                <>
                  <dt className="text-text-muted">Review flags</dt>
                  <dd className="text-text-primary">{approvalReasonText(order.approvalReason) || '—'}</dd>
                </>
              )}
              <dt className="text-text-muted">Placed by</dt>
              <dd className="truncate text-text-primary">{!isStaff && order.createdByRole === 'INTERNAL_STAFF' ? 'TEAMM8 team' : order.createdBy}</dd>
              <dt className="text-text-muted">Placed on</dt>
              <dd className="text-text-primary">{new Date(order.createdAt).toLocaleString()}</dd>
              {isStaff && (
                <>
                  <dt className="text-text-muted">Shopify</dt>
                  <dd className="text-text-primary">
                    {order.dispatched ? 'Order created (sent to warehouse)' : order.shopifyDraftOrderId ? 'Draft order' : import.meta.env.DEV ? 'No draft (dev — writes simulated)' : 'Draft missing — tell an admin'}
                  </dd>
                </>
              )}
            </dl>
            {isStaff && order.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {order.tags.map((t) => (
                  <span key={t} className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>



          {/* Shipping: full panel for staff; tracking-only for clients & reps. */}
          {isStaff && order.status === 'APPROVED' && (
            <div id="shipping" className="scroll-mt-4">
              <ShippingPanel orderId={order.id} onAutoApproved={onShippingAutoApproved} onChanged={onShippingChanged} />
            </div>
          )}

          <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-text-primary">{isStaff ? 'Activity' : 'Order history'}</h3>
            <ol className="space-y-2.5">
              {[...(order.activities || [])].reverse().map((a, i) => {
                const problem = isStaff && PROBLEM_EVENTS.has(a.action);
                return (
                  <li key={i} className="flex gap-2 text-xs">
                    <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${problem ? 'bg-error' : 'bg-accent-subtle'}`} />
                    <div className="min-w-0">
                      <div className={problem ? 'font-medium text-error' : 'text-text-primary'}>{eventLabel(a, order.currency)}</div>
                      <div className="text-text-muted">
                        {a.by} · {new Date(a.at).toLocaleString()}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
            {!isStaff && (
              <p className="mt-3 text-xs text-text-muted">
                Questions about this order?{' '}
                <button onClick={() => navigate('/messages')} className="font-medium text-accent hover:underline">
                  Message the TEAMM8 team
                </button>
              </p>
            )}
            {isStaff && <form onSubmit={submitNote} className="mt-3 flex gap-2">
              <input
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note (staff only)…"
                className="flex-1 rounded-md border border-line px-2.5 py-1.5 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
              <button
                type="submit"
                disabled={noteBusy || !noteText.trim()}
                className="rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
              >
                Add
              </button>
            </form>}
          </div>
        </div>
      </div>
    </>
  );
}
